import { useState, useEffect } from 'react';
import { deriveWorkflowType, getWorkflowMetadata } from '../utils/orchestrationConfigs';

/**
 * Custom hook to fetch and manage workflow metadata
 * @param {Object} message - The message object
 * @param {string} chatId - The chat ID
 * @returns {Object} - Workflow metadata and loading state
 */
export const useWorkflowMetadata = (message, chatId) => {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only fetch for fast-track messages
    if (!message || !chatId || !['fast-track', 'fast-track-shell'].includes(message.action)) {
      // For non-fast-track messages, derive metadata from message data
      if (message && ['fast-track', 'fast-track-shell'].includes(message.action)) {
        const derivedMetadata = getWorkflowMetadata(message);
        setMetadata(derivedMetadata);
      }
      return;
    }

    const fetchMetadata = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/chat/${chatId}/${message.id}/workflow-metadata`);
        
        if (response.ok) {
          const data = await response.json();
          setMetadata(data);
        } else if (response.status === 404) {
          // Orchestration not found, derive from message
          const derivedMetadata = getWorkflowMetadata(message);
          setMetadata(derivedMetadata);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (err) {
        console.warn('Failed to fetch workflow metadata, using derived metadata:', err);
        // Fallback to derived metadata
        const derivedMetadata = getWorkflowMetadata(message);
        setMetadata(derivedMetadata);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [message?.id, message?.action, chatId]);

  return {
    metadata,
    loading,
    error,
    // Helper functions
    isShellWorkflow: metadata?.isShellWorkflow || false,
    workflowType: metadata?.workflowType || deriveWorkflowType(message || {}),
    hasCreativeFile: metadata?.hasCreativeFile || false,
    totalSteps: metadata?.totalSteps || 4,
    activeSteps: metadata?.activeSteps || 4
  };
};

/**
 * Hook to get workflow metadata synchronously (for cases where API call isn't needed)
 * @param {Object} messageOrOrchestration - The message or orchestration data
 * @returns {Object} - Workflow metadata
 */
export const useWorkflowMetadataSync = (messageOrOrchestration) => {
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (messageOrOrchestration) {
      const derivedMetadata = getWorkflowMetadata(messageOrOrchestration);
      setMetadata(derivedMetadata);
    } else {
      // Reset metadata when messageOrOrchestration is null/undefined
      setMetadata(null);
    }
  }, [messageOrOrchestration]);

  return {
    metadata,
    isShellWorkflow: metadata?.isShellWorkflow || false,
    workflowType: metadata?.workflowType || 'fast-track',
    hasCreativeFile: metadata?.hasCreativeFile || false,
    totalSteps: metadata?.totalSteps || 4,
    activeSteps: metadata?.activeSteps || 4
  };
};
